import React, { use, useEffect, useState } from "react";
import Breadcrumb from "../../../core/common/Breadcrumb/breadcrumb";
import type { SliderSingleProps } from "antd";
import { Slider } from "antd";
import { Link, useLocation } from "react-router-dom";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { all_routes } from "../../router/all_routes";
import { Program, Institution } from "../../../core/types/types";
import { formatKey } from "../../../utility/format-text";
import { getCurrencySymbol } from "../../../utility/currencyUtils";
import apiInstance from "../../../utility/apiInstance";
import { useSelector } from "react-redux";
import ApplyPopup from "./applyPopup";
import BackendImage from "../../../core/common/backendImage";
import Pagination from "../../../core/common/pagination/pagination";

const ProgramsGrid = () => {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const isAuthenticated = useSelector(
        (state: any) => state.auth.isAuthenticated
    );
    const [applyPopup, setApplyPopup] = useState<boolean>(false);
    const [selectedProgram, setSelectedProgram] = useState<Program | null>(
        null
    );

    type FilterKey =
        | "searchTerm"
        | "programLevel"
        | "institution"
        | "destination"
        | "field";

    interface FilterValue {
        id: string;
        key: string;
        display: boolean;
    }

    interface FilterValues {
        institution: FilterValue[];
        destination: FilterValue[];
        programLevel: FilterValue[];
        field: FilterValue[];
    }

    const [filterValues, setFilterValues] = useState<FilterValues>({
        institution: [],
        destination: [
            {
                id: "CA",
                key: "Canada",
                display: true,
            },
            {
                id: "US",
                key: "United States",
                display: true,
            },
            {
                id: "GB",
                key: "United Kingdom",
                display: true,
            },
            {
                id: "AU",
                key: "Australia",
                display: true,
            },
            {
                id: "DE",
                key: "Germany",
                display: true,
            },
            {
                id: "IE",
                key: "Ireland",
                display: true,
            },
        ],
        programLevel: [
            {
                id: "undergraduate",
                key: "Under-graduate",
                display: true,
            },
            {
                id: "postgraduate",
                key: "Post-graduate",
                display: true,
            },
            {
                id: "diploma",
                key: "Diploma",
                display: true,
            },
            {
                id: "school",
                key: "Elementary and High School",
                display: true,
            },
            {
                id: "language",
                key: "Language Proficiency",
                display: true,
            },
        ],
        field: [
            {
                id: "arts",
                key: "Arts",
                display: true,
            },
            {
                id: "business",
                key: "Business, Management, Economics",
                display: true,
            },
            {
                id: "school",
                key: "Elementary and High School",
                display: true,
            },
            {
                id: "language",
                key: "Language Proficiency",
                display: true,
            },
            {
                id: "tech",
                key: "Engineering and Technology",
                display: true,
            },
            {
                id: "medicine",
                key: "Health Sciences and Medicine",
                display: true,
            },
            {
                id: "law",
                key: "Law and Politics",
                display: true,
            },
            {
                id: "science",
                key: "Science",
                display: true,
            },
            {
                id: "other",
                key: "Others",
                display: true,
            },
        ],
    });

    interface Filter {
        searchTerm: string;
        programLevel: string[];
        institution: string[];
        destination: string[];
        field: string[];
        page: number;
    }

    const [filters, setFilters] = useState<Filter>({
        searchTerm: searchParams.get("searchTerm") || "",
        programLevel: searchParams.getAll("programLevel"),
        institution: searchParams.getAll("institution"),
        destination: searchParams.getAll("destination"),
        field: searchParams.getAll("field"),
        page: 1,
    });

    const [pagination, setPagination] = useState({
        page: 1,
        totalPages: 1,
        totalDocs: 0
    });

    const [programs, setPrograms] = React.useState<Program[]>([]);

    useEffect(() => {
        const fetchInstitutions = async () => {
            const response = await apiInstance.get("/api/institutions?limit=1000");
            let institutions = response.data.docs;
            institutions = institutions.map((item: Institution) => {
                return {
                    id: item.id,
                    key: item.schoolName,
                    display: true,
                };
            });

            setFilterValues((prev) => ({
                ...prev,
                institution: institutions,
            }));
        };

        fetchInstitutions();
    }, [filters]);

    useEffect(() => {
        const fetchPrograms = async () => {
            try {
                const queryParts: string[] = [];

                queryParts.push(`page=${filters.page}`);
                queryParts.push(`limit=12`);

                if (filters.searchTerm) {
                    queryParts.push(
                        `where[programName][contains]=${encodeURIComponent(filters.searchTerm)}`
                    );
                }

                if (filters.institution.length > 0) {
                    filters.institution.forEach((inst) => {
                        queryParts.push(
                            `where[institution][in][]=${encodeURIComponent(inst)}`
                        );
                    });
                }

                if (filters.destination.length > 0) {
                    filters.destination.forEach((co) => {
                        queryParts.push(
                            `where[institution.country][in][]=${encodeURIComponent(co)}`
                        );
                    });
                }

                if (filters.programLevel.length > 0) {
                    filters.programLevel.forEach((pl) => {
                        queryParts.push(
                            `where[programLevel][in][]=${encodeURIComponent(pl)}`
                        );
                    });
                }

                if (filters.field.length > 0) {
                    filters.field.forEach((fld) => {
                        queryParts.push(
                            `where[field][in][]=${encodeURIComponent(fld)}`
                        );
                    });
                }

                const query =
                    queryParts.length > 0 ? `?${queryParts.join("&")}` : "";

                const res = await apiInstance.get(`/api/programs${query}`);
                console.log("res", res)

                setPagination({
                    page: res.data.page,
                    totalPages: res.data.totalPages,
                    totalDocs: res.data.totalDocs
                });

                setPrograms(res.data.docs);
            } catch (err) {
                console.error("Failed to fetch program details:", err);
            }
        };

        fetchPrograms();
    }, [filters]);

    const handlePageChange = (newPage: number) => {
        setFilters(prev => ({ ...prev, page: newPage }));
        window.scrollTo(0, 0);
    };

    const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFilters((prevFilters) => ({
            ...prevFilters,
            [name]: value,
            page: 1,
        }));
    };

    const handleMultipleFilterChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const { name, value, checked } = e.target;
        const key = name as FilterKey;

        setFilters((prevFilters: Filter) => {
            const currentValues = prevFilters[key] as string[];
            if (checked) {
                return {
                    ...prevFilters,
                    [name]: [...currentValues, value],
                };
            } else {
                return {
                    ...prevFilters,
                    [name]: currentValues.filter(
                        (item: String) => item !== value
                    ),
                };
            }
        });
    };

    const handleFilterSearchChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        name: keyof FilterValues
    ) => {
        const { value } = e.target;
        setFilterValues((prev) => ({
            ...prev,
            [name]: prev[name].map((item: FilterValue) => ({
                ...item,
                display: item.key.toLowerCase().includes(value.toLowerCase()),
            })),
        }));
    };

    const clearFilters = () => {
        setFilters({
            searchTerm: "",
            programLevel: [],
            institution: [],
            destination: [],
            field: [],
            page: 1,
        });
    };

    return (
        <>
            <Breadcrumb title="All Programs" />
            {/* Course */}
            <section className="course-content">
                <div className="container">
                    <div className="row align-items-baseline">
                        <div className="col-lg-3 theiaStickySidebar position-relative mb-3">
                            <div className="filter-clear">
                                <div className="clear-filter mb-4 pb-lg-2 d-flex align-items-center justify-content-between">
                                    <h5>
                                        <i className="feather-filter me-2" />
                                        Filters
                                    </h5>
                                    <div
                                        onClick={clearFilters}
                                        className="clear-text cursor-pointer"
                                    >
                                        Clear
                                    </div>
                                </div>
                                <div className="accordion accordion-customicon1 accordions-items-seperate">
                                    {Object.entries(filterValues).map(
                                        ([key, items]) => (
                                            <div className="accordion-item">
                                                <h2
                                                    className="accordion-header"
                                                    id="headingcustomicon1One"
                                                >
                                                    <Link
                                                        to="#"
                                                        className="accordion-button"
                                                        data-bs-toggle="collapse"
                                                        data-bs-target="#collapsecustomicon1One"
                                                        aria-expanded="false"
                                                        aria-controls="collapsecustomicon1One"
                                                    >
                                                        {formatKey(key)}{" "}
                                                        <i className="fa-solid fa-chevron-down" />
                                                    </Link>
                                                </h2>
                                                <div
                                                    id="collapsecustomicon1One"
                                                    className="accordion-collapse collapse show"
                                                    aria-labelledby="headingcustomicon1One"
                                                    data-bs-parent="#accordioncustomicon1Example"
                                                    style={{}}
                                                >
                                                    <div className=" search-group m-3">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Search"
                                                            onChange={(e) => {
                                                                handleFilterSearchChange(
                                                                    e,
                                                                    key as keyof FilterValues
                                                                );
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="accordion-body">
                                                        {items.map(
                                                            (
                                                                item: FilterValue
                                                            ) => (
                                                                <div
                                                                    className={
                                                                        item.display
                                                                            ? ""
                                                                            : "apb-hidden"
                                                                    }
                                                                >
                                                                    <label className="custom_check">
                                                                        <input
                                                                            type="checkbox"
                                                                            name={
                                                                                key
                                                                            }
                                                                            value={
                                                                                item.id
                                                                            }
                                                                            // checked={filters[
                                                                            //     key as keyof Filter
                                                                            // ].includes(
                                                                            //     item.id
                                                                            // )}
                                                                            checked={
                                                                                Array.isArray(filters[key as keyof Filter])
                                                                                    ? (filters[key as keyof Filter] as string[]).includes(item.id)
                                                                                    : false
                                                                            }
                                                                            onChange={
                                                                                handleMultipleFilterChange
                                                                            }
                                                                        />
                                                                        <span className="checkmark" />{" "}
                                                                        {
                                                                            item.key
                                                                        }
                                                                    </label>
                                                                </div>
                                                            )
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-9">
                            {/* Filter */}
                            <div className="showing-list mb-4">
                                <div className="row align-items-center">
                                    <div className="col-lg-12">
                                        <div className="show-filter add-course-info">
                                            <form action="#">
                                                <div className="d-sm-flex justify-content-center justify-content-lg-end mb-1 mb-lg-0">
                                                    <div className=" search-group">
                                                        <i className="isax isax-search-normal-1" />
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Search"
                                                            name="searchTerm"
                                                            onChange={
                                                                handleFilterChange
                                                            }
                                                            value={
                                                                filters.searchTerm
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* /Filter */}
                            <div className="row">
                                {programs.length > 0
                                    ? programs.map((program: Program) => (
                                        <div className="col-xl-4 col-md-6">
                                            <div className="course-item-two course-item mx-0">
                                                <div className="course-img">
                                                    <BackendImage
                                                        src={program?.image?.url}
                                                        alt="img"
                                                        className="img-fluid"
                                                        fallback="/assets/img/course-placeholder.png"
                                                    />
                                                </div>
                                                <div className="course-content">
                                                    <div className="d-flex justify-content-between mb-2">
                                                        <div className="d-flex align-items-center">
                                                            <div>
                                                                {program
                                                                    ?.institution
                                                                    ?.schoolName ||
                                                                    ""}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <h6 className="title mb-2" title={program?.programName || ""}>
                                                        {program?.programName ||
                                                            ""}
                                                    </h6>
                                                    <p className="d-flex align-items-center mb-3">
                                                        {program?.institution
                                                            ?.location || ""}
                                                    </p>
                                                    <p className="d-flex align-items-center mb-3 fw-bold">
                                                        {program?.programLevel ||
                                                            ""}
                                                    </p>
                                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                                        <h6 className="text-primary mb-0">
                                                            Application Fee
                                                        </h6>
                                                        <span>
                                                            {program?.applicationFee
                                                                ? `${getCurrencySymbol(program.institution?.country)}${program.applicationFee}`
                                                                : "Free"}
                                                        </span>
                                                    </div>
                                                    <div className="d-flex align-items-center justify-content-between">
                                                        <div>
                                                            <h6 className="text-primary mb-0">
                                                                Tuition Fee
                                                            </h6>
                                                            <small className="text-muted">
                                                                (First year)
                                                            </small>
                                                        </div>
                                                        <span>
                                                            {program?.tuitionFee
                                                                ? `${getCurrencySymbol(program.institution?.country)}${program.tuitionFee}`
                                                                : "Free"}
                                                        </span>
                                                    </div>
                                                    <hr></hr>
                                                    <Link
                                                        to={all_routes.programDetails.replace(
                                                            ":id",
                                                            program.id
                                                        )}
                                                        className="btn mt-2 w-100 btn-primary font-heading icon-send-letter"
                                                    >
                                                        View Details
                                                        <i className="isax isax-arrow-right-3 ms-1" />
                                                    </Link>

                                                    <>
                                                        <button
                                                            className="btn mt-2 w-100 btn-secondary font-heading icon-send-letter"
                                                            onClick={() => {
                                                                if (
                                                                    !isAuthenticated
                                                                ) {
                                                                    // redirect to login
                                                                    window.location.href = all_routes.login
                                                                    return;
                                                                }

                                                                setSelectedProgram(
                                                                    program
                                                                );
                                                                setApplyPopup(
                                                                    true
                                                                );
                                                            }}
                                                        >
                                                            Apply for the
                                                            Program
                                                        </button>
                                                    </>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                    : "No programs found for this search criteria."}
                            </div>

                            {/* pagination */}
                            {pagination.totalPages > 1 && (
                                <Pagination
                                    page={pagination.page}
                                    totalPages={pagination.totalPages}
                                    setPage={(value) => {
                                        const newPage =
                                            typeof value === "function"
                                                ? value(pagination.page)
                                                : value;

                                        handlePageChange(newPage);
                                    }}
                                    hasPrevPage={pagination.page > 1}
                                    hasNextPage={pagination.page < pagination.totalPages}
                                />
                            )}
                            {/* /pagination */}
                        </div>
                    </div>
                </div>
                <ApplyPopup
                    show={applyPopup}
                    onClose={() => setApplyPopup(false)}
                    program={selectedProgram}
                />
            </section>
            {/* /Course */}
        </>
    );
};

export default ProgramsGrid;
